import { publicHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";

/* ════════════════════════════════════════════════════════════
 *  웹(PC·모바일 브라우저) 전용 애플 로그인 — Apple JS SDK 팝업 방식
 *
 *  앱(UA: MT_I / MT_A)은 네이티브 브릿지가 처리하므로 이 파일을 타지 않는다.
 *  SDK 는 index.html 에서 appleid.auth.js 로 로드된다.
 *
 *  ⚠️ 다른 SNS 처럼 AUTH_URL 풀 redirect 를 쓰지 않는 이유:
 *     애플은 response_mode 가 form_post 라 콜백이 POST 로 온다. 정적 프론트 라우트는
 *     POST 를 받을 수 없고, 백엔드에도 그 콜백을 받는 엔드포인트가 없다.
 * ════════════════════════════════════════════════════════════ */

interface AppleIDConfig {
  clientId: string;
  redirectURI: string;
  scope: string;
  state: string;
  nonce: string;
  usePopup: boolean;
}

interface AppleIDSignInResponse {
  authorization: {
    id_token: string;
    code: string;
    state: string;
  };
  // 최초 로그인 1회만 내려옴 (이후 재로그인 시 없음)
  user?: {
    name: { firstName: string; lastName: string };
    email: string;
  };
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: AppleIDConfig) => void;
        signIn: () => Promise<AppleIDSignInResponse>;
      };
    };
  }
}

/** 백엔드 /oauth/apple-{customer,business} 응답 */
interface AppleValidationResponse {
  statusCode: number;
  data: {
    appleId: string | null;
    email: string | null;
    valid: boolean;
    type: string | null;
  } | null;
  message?: string;
}

export interface AppleWebSignInResult {
  appleId: string;
  email: string | null;
}

const appleErrorCode = (error: unknown): string | undefined =>
  (error as { error?: string } | null)?.error;

/** 사용자가 팝업을 닫거나 취소한 경우 — 에러 안내 없이 조용히 종료해야 함 */
export const isAppleUserCancel = (error: unknown): boolean => {
  const code = appleErrorCode(error);
  return code === "popup_closed_by_user" || code === "user_cancelled_authorize";
};

/** 브라우저 팝업 차단 — 일반 실패와 안내 문구가 달라야 함 (iOS 사파리에서 자주 발생) */
export const isApplePopupBlocked = (error: unknown): boolean =>
  appleErrorCode(error) === "popup_blocked_by_browser";

let initialized = false;

const randomToken = () => Math.random().toString(36).substring(2, 15);

const initAppleSDK = () => {
  if (initialized || !window.AppleID) return;

  window.AppleID.auth.init({
    clientId: import.meta.env.VITE_APPLE_CLIENTID,
    // 팝업(web_message) 방식이라 실제로 이 URL 로 이동하지는 않지만,
    // Apple 이 Service ID 에 등록된 Return URL 인지 대조하므로 반드시 넘겨야 한다.
    redirectURI: import.meta.env.VITE_APPLE_REDIRECT,
    scope: "name email",
    state: randomToken(),
    nonce: randomToken(),
    usePopup: true,
  });

  initialized = true;
};

/**
 * 애플 팝업 로그인 → 백엔드 토큰 검증까지 수행하고 appleId 를 돌려준다.
 *
 * ⚠️ 팝업 차단을 피하려면 클릭 핸들러에서 다른 await 없이 곧바로 호출해야 한다.
 *    (SDK 가 signIn() 안에서 window.open 을 동기 호출하기 때문)
 */
export const signInWithAppleWeb = async (): Promise<AppleWebSignInResult> => {
  if (!window.AppleID) {
    throw new Error("Apple 로그인 SDK 를 불러오지 못했습니다.");
  }

  initAppleSDK();

  const response = await window.AppleID.auth.signIn();
  const identityToken = response.authorization?.id_token;

  if (!identityToken) {
    throw new Error("Apple 인증 토큰을 받지 못했습니다.");
  }

  // 서명·aud·iss 검증은 백엔드가 수행 (application-*.yml 의 allowed-client-ids 대조)
  const endpoint =
    import.meta.env.VITE_APP_TYPE === "business"
      ? API_ENDPOINTS.OAUTH.APPLE_BUSINESS
      : API_ENDPOINTS.OAUTH.APPLE_CUSTOMER;

  const { data } = await publicHttp.post<AppleValidationResponse>(endpoint, {
    identityToken,
  });

  // 검증 실패도 HTTP 200 + body statusCode 4000 으로 내려오므로 body 로 판별한다.
  if (data.statusCode !== 200 || !data.data?.valid || !data.data.appleId) {
    throw new Error(data.message || "Apple 토큰 검증에 실패했습니다.");
  }

  return {
    appleId: data.data.appleId,
    // 애플은 재로그인 시 이메일을 안 주거나 비공개 릴레이 주소를 준다.
    email: data.data.email ?? null,
  };
};
